import { useEffect, lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { ComingSoon, LoadingScreen, RequireAdmin } from '~/components';
import { accountThunks } from '~/slices';
import { useDispatch, useSelector } from '~/hooks';
import { webEndpoints } from '~/endpoints';

const Main = lazy(() =>
  import('./Main/Main').then((module) => ({ default: module.Main })),
);
const Chat = lazy(() =>
  import('./Main/Chat/Chat').then((module) => ({ default: module.Chat })),
);
const Computers = lazy(() =>
  import('./Main/Computers/Computers').then((module) => ({
    default: module.Computers,
  })),
);
const ScheduledTasks = lazy(() =>
  import('./Main/SceduledTasks/ScheduledTasks').then((module) => ({
    default: module.ScheduledTasks,
  })),
);
const ClaimYourToken = lazy(() =>
  import('./Main/ClaimYourToken/ClaimYourToken').then((module) => ({
    default: module.ClaimYourToken,
  })),
);
const Subscription = lazy(() =>
  import('./Main/Subscription/Subscription').then((module) => ({
    default: module.Subscription,
  })),
);
const Admin = lazy(() =>
  import('./Main/Admin/Admin').then((module) => ({ default: module.Admin })),
);
const Login = lazy(() =>
  import('./Login/Login').then((module) => ({ default: module.Login })),
);

const router = createBrowserRouter([
  {
    path: webEndpoints.auth.path,
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: webEndpoints.index.path,
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Main />
      </Suspense>
    ),
    children: [
      {
        path: webEndpoints.index.path,
        element: <Navigate to={webEndpoints.chatById.to('new')} />,
      },
      {
        path: webEndpoints.chatById.path,
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Chat />
          </Suspense>
        ),
      },
      {
        path: webEndpoints.computers.path,
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Computers />
          </Suspense>
        ),
      },
      {
        path: webEndpoints.scheduledTasks.path,
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <ScheduledTasks />
          </Suspense>
        ),
      },
      {
        path: webEndpoints.addInternalServer.path,
        element: <ComingSoon />,
        // element: <AddInternalServer />,
      },
      {
        path: webEndpoints.claimYourToken.path,
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <ClaimYourToken />
          </Suspense>
        ),
      },
      {
        path: webEndpoints.subscription.path,
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Subscription />
          </Suspense>
        ),
      },
      {
        path: webEndpoints.admin.path,
        element: <RequireAdmin />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <Admin />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={webEndpoints.chatById.to('new')} replace />,
  },
]);

export const Router = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.account);

  useEffect(() => {
    dispatch(accountThunks.refresh());
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return <RouterProvider router={router} />;
};
